import { LinkEntryRow } from '@/components/admin/liens/link-entry-row';
import { ReferenceField } from '@/components/admin/reglages/reference-list';
import { Button } from '@/components/ui/button';
import { RequiredMark } from '@/components/ui/required-mark';
import type { LinkFormDraft } from '@/lib/link-form';
import {
  addLinkRow,
  canSubmitLink,
  editLinkRow,
  linkExperienceLookupOf,
  linkFormProblems,
  removeLinkRow,
} from '@/lib/link-form';
import { LINK_LIFETIME_LABEL } from '@/lib/link-labels';
import type { ExperienceCard, LinkEntryProblem, LinkLifetime } from '@matter_extractor/shared';
import { LINK_ENTRIES_MAX, LINK_LIFETIMES, LINK_NAME_MAX } from '@matter_extractor/shared';
import { Plus } from 'lucide-react';
import { useId, useState } from 'react';

/**
 * The form of a link (§11.4, planche `ReglagesPartage`) - the SAME one for the
 * creation and for « Modifier ».
 *
 * One component and not two, because §5.1 makes the two gestures write the same
 * three things (« tout est éditable après coup sauf le secret ») and because the
 * entries block is where every rule of this screen lives: written twice, the two
 * copies would have diverged on the refusal before the lot shipped - the
 * argument `reference-list.tsx` makes about the two sections it serves.
 *
 * ## The draft is state, initialised from props and NEVER by an effect
 *
 * The page mounts it with a `key`, so « repartir de zéro » is a REMOUNT - the
 * régime of `involvement-scale.tsx` and of `versioned-editor.tsx`. That is what
 * lets a creation clear itself once it has landed, and a « Modifier » reopen on
 * the base rather than on what was typed before a refusal.
 *
 * ## Two refusals, and the button is greyed by whichever is in hand
 *
 * The local one comes from the experience cards the page already read, so the
 * button greys at the keystroke rather than at the round trip. The server's
 * comes back in a 409 and is the authority - the cards can be a colleague's
 * gesture out of date. They are merged by INDEX, and the server's wins on a row
 * both name: it is the one that just refused the write.
 *
 * While the library is still in flight the local walk is SKIPPED entirely, and
 * that is deliberate: `linkEntryProblems` answers `experience-unknown` for
 * every id it cannot resolve, so running it on an empty list would accuse every
 * row of the form. Same flag, same reason, as `criteriaRead` in the protocol
 * editor.
 */

export interface LinkFormProps {
  readonly initial: LinkFormDraft;
  readonly experiences: readonly ExperienceCard[];
  /** False while the library is in flight - an empty list must not ACCUSE. */
  readonly experiencesRead: boolean;
  readonly submitLabel: string;
  readonly submitting: boolean;
  /** The rows the SERVER refused, empty when it has refused nothing. */
  readonly serverProblems: readonly LinkEntryProblem[];
  /** A transport failure, worded by the page - never a refusal. */
  readonly failure: string | null;
  readonly testId: string;
  readonly onSubmit: (draft: LinkFormDraft) => void;
  /** Present on « Modifier » only: a creation has nothing to go back to. */
  readonly onCancel?: (() => void) | undefined;
}

export function LinkForm({
  initial,
  experiences,
  experiencesRead,
  submitLabel,
  submitting,
  serverProblems,
  failure,
  testId,
  onSubmit,
  onCancel,
}: LinkFormProps) {
  const [draft, setDraft] = useState<LinkFormDraft>(initial);
  const lifetimeId = useId();

  const localProblems = experiencesRead
    ? linkFormProblems(draft, linkExperienceLookupOf(experiences))
    : [];
  const problems = mergeProblems(localProblems, serverProblems);
  const armed = canSubmitLink(draft, problems) && !submitting;

  return (
    <form
      data-testid={testId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (armed) onSubmit(draft);
      }}
      className="space-y-5"
    >
      <div className="max-w-xl">
        <ReferenceField
          label="Nom"
          testId="partage-nom"
          value={draft.name}
          max={LINK_NAME_MAX}
          placeholder="Atelier du 24 septembre"
          onChange={(name) => setDraft((current) => ({ ...current, name }))}
        />
        <p className="pt-1 text-gray-500 text-xs">Pour vous : la personne ne le voit jamais.</p>
      </div>

      <div className="max-w-xs space-y-1">
        <label htmlFor={lifetimeId} className="block font-semibold text-gray-800 text-xs">
          Durée de vie
          <RequiredMark />
        </label>
        <select
          id={lifetimeId}
          data-testid="partage-duree"
          value={draft.lifetime}
          disabled={submitting}
          onChange={(event) => {
            /*
              The value is read HERE and not inside the updater. React nulls
              `currentTarget` the moment the handler returns, and a `setState`
              updater runs at the NEXT render - so reading it in there threw
              « Cannot read properties of null » on the first change of this
              select, and took the whole screen down with it. Found by the e2e
              suite: the two other selects of this form pass their value
              straight to a callback, so nothing else showed it.
            */
            const lifetime = event.currentTarget.value as LinkLifetime;
            setDraft((current) => ({ ...current, lifetime }));
          }}
          className="h-10 w-full rounded-md border-2 border-gray-300 bg-white px-3 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          {LINK_LIFETIMES.map((lifetime) => (
            <option key={lifetime} value={lifetime}>
              {LINK_LIFETIME_LABEL[lifetime]}
            </option>
          ))}
        </select>
        <p className="text-gray-500 text-xs">
          À partir de la création. Prolongeable ensuite depuis la liste.
        </p>
      </div>

      <fieldset className="space-y-3">
        <legend className="font-semibold text-gray-800 text-xs">
          Entrées
          <RequiredMark />
        </legend>
        <ul className="space-y-4">
          {draft.entries.map((row, index) => (
            <LinkEntryRow
              // The row's LOCAL key and not its index: two rows may name the
              // same experience under the same tag, and an index key would
              // remount a row under the cursor when one above it goes.
              key={row.key}
              row={row}
              index={index}
              experiences={experiences}
              problem={problems.find((problem) => problem.index === index) ?? null}
              removable={draft.entries.length > 1}
              disabled={submitting}
              onChange={(patch) => setDraft((current) => editLinkRow(current, index, patch))}
              onRemove={() => setDraft((current) => removeLinkRow(current, index))}
            />
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="partage-ajouter-entree"
          disabled={submitting || draft.entries.length >= LINK_ENTRIES_MAX}
          onClick={() => setDraft((current) => addLinkRow(current))}
        >
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          Ajouter une entrée
        </Button>
        <p className="text-gray-500 text-xs">
          Une seule entrée : le lien démarre directement. Plusieurs : la page de choix montre le nom
          et la courte description de chacune.
        </p>
      </fieldset>

      {/* Mounted from the first render, empty or not: a live region created at
          the same moment it fills is not read out. */}
      <p aria-live="polite" className="text-rouge-deep text-sm">
        {failure}
      </p>

      <div className="flex items-center gap-3">
        <Button type="submit" data-testid="partage-soumettre" disabled={!armed}>
          {submitLabel}
        </Button>
        {onCancel === undefined ? null : (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Annuler
          </Button>
        )}
      </div>
    </form>
  );
}

/**
 * One list of refusals out of two, the SERVER's winning on a row both name.
 *
 * Both are true statements about the same row and the server's is the fresher:
 * it was computed against the base a moment ago, the local one against cards
 * that may be a colleague's gesture old.
 */
function mergeProblems(
  local: readonly LinkEntryProblem[],
  server: readonly LinkEntryProblem[],
): LinkEntryProblem[] {
  const byIndex = new Map<number, LinkEntryProblem>();
  for (const problem of local) byIndex.set(problem.index, problem);
  for (const problem of server) byIndex.set(problem.index, problem);
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}
